import { IModel, IAttribute, IViolation, ModelConstants, ModelMarker } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';

import { Pipeline } from 'app/model/pipeline/pipeline';
import { format, sanitizeAndFormatString } from 'app/shared/utils/string.util';
import { ForEachActivity } from './foreach.activity';
import { ValidationActivity } from './validation.activity';

@Metamodel.model({
    name: ModelConstants.ifConditionActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.IfConditionActivityLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.IfConditionActivityLabel)
    ],
    referable: true,
    validator:  IfConditionActivity.unallowedTypesValidator
})
export class IfConditionActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly EXPRESSION: IAttribute;

    // This property should always be an Expression type.
    @Metamodel.attribute({
        display: stringResources.Expression,
        templatable: true,
        accepts: ['Bool'],
        association: 'EXPRESSION',
        constraint: {
            required: true
        }
    })
    public expression: string;

    @Metamodel.attribute({
        display: stringResources.Pipeline,
        constraint: {
            required: true,
            validator: IfConditionActivity.uniqueActivitiesValidator
        },
        type: Pipeline.MODEL
    })
    public ifTrueActivityPipeline?: Pipeline;

    @Metamodel.attribute({
        display: stringResources.Pipeline,
        constraint: {
            required: true
        },
        type: Pipeline.MODEL
    })
    public ifFalseActivityPipeline?: Pipeline;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ControlActivity';
    }

    private static uniqueActivitiesValidator(ifConditionActivity?: IfConditionActivity): IViolation[] | undefined {
        const falseActivitySet = new Set();
        const violations: IViolation[] = [];

        if (ifConditionActivity.ifFalseActivityPipeline) {
            for (const activity of ifConditionActivity.ifFalseActivityPipeline.activities) {
                falseActivitySet.add(activity.name);
            }
        }

        if (ifConditionActivity.ifTrueActivityPipeline) {
            for (const activity of ifConditionActivity.ifTrueActivityPipeline.activities) {
                if (falseActivitySet.has(activity.name)) {
                    violations.push({
                        context: ifConditionActivity,
                        message: () => sanitizeAndFormatString(stringResources.IfConditionActivitiesUniqueError, activity.name, ifConditionActivity.name)
                    });
                }
            }
        }

        return violations.length ? violations : undefined;
    }

    private static unallowedTypesValidator(ifActivity?: IfConditionActivity): IViolation[] {
        const violations: IViolation[] = [];

        if (ifActivity) {
            if (ifActivity.ifFalseActivityPipeline) {
                violations.push(...IfConditionActivity.findUnallowedTypeViolation(ifActivity.ifFalseActivityPipeline.activities));
            }

            if (ifActivity.ifTrueActivityPipeline) {
                violations.push(...IfConditionActivity.findUnallowedTypeViolation(ifActivity.ifTrueActivityPipeline.activities));
            }
        }

        return violations;
    }

    private static findUnallowedTypeViolation(activities: Set<Activity>): IViolation[] {
        const unallowedTypes = [ForEachActivity, IfConditionActivity, ValidationActivity];
        const violations: IViolation[] = [];

        for (const activity of activities) {
            if (unallowedTypes.find((activityType) => activity instanceof activityType)) {
                const label = Metamodel.modelOf(activity).display[0];

                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(stringResources.IfConditionActivityUnallowedActvitiyError, label, activity.name),
                    revalidate: () => ModelMarker.isDeleted(activity)
                });
            }
        }

        return violations;
    }

    public onActivityCloned(clonedActivity: IfConditionActivity, context: { inlineDataset?: boolean }): void {
        if (this.ifTrueActivityPipeline.activities != null) {
            this.ifTrueActivityPipeline.activities.forEach((activity) => {
                const clonedifTrueActivity = clonedActivity.ifTrueActivityPipeline?.activities?.find((clonedifTrueActivity) => clonedifTrueActivity.name === activity.name);
                if (clonedifTrueActivity != null && activity instanceof Activity) {
                    activity.onActivityCloned(clonedifTrueActivity, context);
                }
            })
        }

        if (this.ifFalseActivityPipeline.activities != null) {
            this.ifFalseActivityPipeline.activities.forEach((activity) => {
                const clonedifFalseActivity = clonedActivity.ifFalseActivityPipeline?.activities?.find((clonedifFalseActivity) => clonedifFalseActivity.name === activity.name);
                if (clonedifFalseActivity != null && activity instanceof Activity) {
                    activity.onActivityCloned(clonedifFalseActivity, context);
                }
            })
        }
    }
}
